import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/components/Common";
import { Page, PageHeader, Loading, ErrorNote, RichText, Flourish } from "@/components/Common";

export const ARTICLE_CATS = ["سب", "تنقیدی مضامین", "تحقیقی مقالات", "ادبی مباحث", "ادبی نظریات"];

export function ArticlesPage() {
  const [articles, setArticles] = useState(null);
  const [cat, setCat] = useState("سب");

  useEffect(() => {
    setArticles(null);
    api.get("/articles", { params: cat !== "سب" ? { category: cat } : {} }).then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, [cat]);

  return (
    <Page>
      <PageHeader title="تنقید و تحقیق" subtitle="تنقیدی مضامین، تحقیقی مقالات، ادبی مباحث اور نظریات" crumbs={[{ label: "تنقید و تحقیق" }]} />
      <div className="flex flex-wrap gap-2 mb-8" data-testid="article-category-tabs">
        {ARTICLE_CATS.map((c) => (
          <button
            key={c}
            data-testid={`article-tab-${c}`}
            onClick={() => setCat(c)}
            className={`px-5 py-1.5 rounded-full border text-base leading-[2] transition-colors ${
              cat === c ? "bg-[#2a1508] text-[#f5efe3] border-[#2a1508]" : "border-[#c5a059]/60 text-[#5c3a21] hover:border-[#d4af37]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>
      {!articles ? (
        <Loading />
      ) : articles.length === 0 ? (
        <p className="text-center text-lg text-[#5c3a21] py-16" data-testid="articles-empty">اس زمرے میں مضامین جلد شامل کیے جائیں گے</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="articles-grid">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/articles/${a.slug}`}
              data-testid={`article-card-${a.slug}`}
              className="group bg-[#fdfbf7] border border-[#d4af37]/40 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(42,21,8,0.12)] hover:border-[#d4af37]"
            >
              <span className="inline-block text-sm px-3 py-0.5 rounded-full bg-[#e8dfcc] text-[#4a2b16] border border-[#c5a059]/50 leading-[2]">{a.category}</span>
              <h3 className="mt-3 text-2xl font-bold text-[#2a1508] leading-[2]">{a.title}</h3>
              <p className="mt-2 text-base sm:text-lg text-[#5c3a21] leading-[2.2] line-clamp-2">{a.excerpt}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-[#2a1508] group-hover:text-[#c5a059] transition-colors">مکمل مضمون <ArrowLeft size={15} /></span>
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setArticle(null);
    setErr(false);
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <Page><ErrorNote message="مضمون نہیں ملا" /></Page>;
  if (!article) return <Page><Loading /></Page>;

  return (
    <Page className="max-w-4xl">
      <PageHeader title={article.title} crumbs={[{ label: "تنقید و تحقیق", to: "/articles" }, { label: article.title }]} />
      <div className="flex flex-wrap items-center gap-3 mb-8 text-base text-[#5c3a21]">
        <span className="px-3 py-0.5 rounded-full bg-[#e8dfcc] border border-[#c5a059]/50 leading-[2]">{article.category}</span>
        {article.author && <span className="leading-[2]">از: {article.author}</span>}
      </div>
      <article className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-2xl p-6 sm:p-10 shadow-[0_4px_20px_rgba(42,21,8,0.06)]" data-testid="article-content">
        <RichText text={article.content} />
        <Flourish className="w-44 mx-auto mt-10 text-[#c5a059]" />
      </article>
    </Page>
  );
}
